import type { AdminScope } from '@/shared/constants/admin-scope'
import { useParams } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import { useDestinationByIdQuery } from '@/entities/destination'
import { useLanguagesQuery } from '@/entities/tenant'
import { AdminScopeType } from '@/shared/constants/admin-scope'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useMediaUploadUi } from '@/shared/hooks/use-media-upload-ui'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { buildScopedAdminPath } from '@/shared/libs/admin-routes'
import { useDestinationFormActions } from './use-destination-form-actions'
import { useDestinationFormState } from './use-destination-form-state'

/**
 * Everything the destination create/edit page needs, composed from focused hooks:
 * scope/permissions, form values + dirty tracking, reference data, media UI and save actions.
 */
export function useDestinationForm() {
  const params = useParams({ strict: false })
  const { isTenantAdmin, scopeType, tenantSlug } = useAdminScope()
  const destinationId = params.destinationId ?? ''
  const isEdit = Boolean(destinationId)
  // * VI: Destination là dữ liệu master dùng chung; admin của tenant chỉ được XEM destination đã có, không được sửa. Super admin mới tạo/sửa. | EN: Destinations are shared master data; a tenant admin may only VIEW an existing destination, never change it. Only a super admin creates/edits.
  const isReadonly = isTenantAdmin && isEdit

  const currentScope: AdminScope =
    scopeType === AdminScopeType.Tenant && tenantSlug
      ? { type: AdminScopeType.Tenant, tenantSlug }
      : { type: AdminScopeType.Super }
  const listPath = buildScopedAdminPath(currentScope, '/destinations')

  const destinationQuery = useDestinationByIdQuery(destinationId)
  const languagesQuery = useLanguagesQuery(true)
  const supportedLanguages = useMemo(
    () =>
      (languagesQuery.data?.data ?? [])
        .filter((item) => item.isActive && item.code?.trim())
        .map((item) => item.code.trim()),
    [languagesQuery.data?.data],
  )

  const state = useDestinationFormState({
    isEdit,
    isReadonly,
    destinationId,
    destination: destinationQuery.data?.data,
    supportedLanguages,
  })
  const countriesQuery = useCountriesQuery({})
  const provincesQuery = useProvincesQuery({ countryId: state.form.countryId })
  const media = useMediaUploadUi()
  const unsavedChanges = useUnsavedChangesGuard(state.hasUnsavedChanges)
  const actions = useDestinationFormActions({
    destinationId,
    isEdit,
    listPath,
    form: state.form,
    translations: state.translations,
    supportedLanguages,
    isMediaUploading: media.isMediaUploading,
    clearCurrentJobs: state.clearCurrentJobs,
    allowNextNavigation: unsavedChanges.allowNextNavigation,
    commitBaseline: state.commitBaseline,
  })

  return {
    isEdit,
    isReadonly,
    listPath,
    isLoadingDestination: destinationQuery.isLoading,
    isLoadingLanguages: languagesQuery.isLoading,
    supportedLanguages,
    countries: countriesQuery.data?.data ?? [],
    provinces: provincesQuery.data?.data ?? [],
    state,
    media,
    actions,
    isSaving: actions.isSaving || languagesQuery.isLoading,
    unsavedChangesDialog: unsavedChanges.dialog,
  }
}
